import { useEffect, useMemo, useState } from "react"

import { InteractionRef, InteractionStatus } from "@vertesia/common"
import { FilterX, ListFilter, X } from "lucide-react"

import { useUserSession } from "@session/UserSession"

import { Badge } from "@ui/badge"
import { Button } from "@ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@ui/card"
import { Input } from "@ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/tooltip"

interface AvailableServicesPanelProps {
    onInteractionSelected: (interaction: InteractionRef) => void
}
export function AvailableServicesPanel({ onInteractionSelected }: Readonly<AvailableServicesPanelProps>) {
    const { client, isLoading, user } = useUserSession()

    const [open, setOpen] = useState(false)
    const [filterMode, setFilterMode] = useState<"tags" | "version" | "status" | undefined>(undefined)
    const [tagsValue, setTagsValue] = useState("")
    const [tags, setTags] = useState<string[]>([])
    const [versionValue, setVersionValue] = useState<number>(0)
    const [version, setVersion] = useState<number | undefined>(undefined)
    const [status, setStatus] = useState<InteractionStatus | undefined>(undefined)

    const [uniqueTags, setUniqueTags] = useState<string[]>([])
    const [selectedTags, setSelectedTags] = useState<string[]>([])

    const [filterValue, setFilterValue] = useState("")
    const [interactions, setInteractions] = useState<InteractionRef[]>([])

    const filteredOptions = useMemo(() => {
        return interactions.filter((interaction) => {
            const matchesName = interaction.name.toLowerCase().includes(filterValue.toLowerCase())
            const matchesSelectedTags = selectedTags.length === 0 || selectedTags.every((selectedTag) => interaction.tags?.includes(selectedTag))

            return matchesName && matchesSelectedTags
        })
    }, [interactions, filterValue, selectedTags])

    const handleClearFilters = () => {
        setOpen(false)
        setFilterMode(undefined)
        setStatus(undefined)
        setTagsValue("")
        setTags([])
        setVersionValue(0)
        setVersion(undefined)
    }

    const handleStatusChange = (value: InteractionStatus) => {
        setOpen(false)
        setStatus(value)
    }

    const handleVersionValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        const parsedValue = parseFloat(e.target.value)
        setVersionValue(!isNaN(parsedValue) ? parsedValue : 0)
    }

    const handleVersionInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
        if (e.key === "Enter") {
            setOpen(false)
            setVersion(versionValue)
            setVersionValue(0)
        }
    }

    const handleTagsInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
        if (e.key === "Enter" && tagsValue.trim() !== "") {
            setOpen(false)
            setTags(
                tagsValue
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter((tag) => tag !== "")
            )
        }
    }

    const handleUniqueTagSelection = (tag: string) => {
        if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter((t) => t !== tag))
        } else {
            setSelectedTags([...selectedTags, tag])
        }
    }

    const searchInteractions = () => {
        if (!client || isLoading) {
            return
        }

        const query: any = {
            status,
            tags: tags.length > 0 ? tags : undefined,
            version,
        }
        client.interactions.list({ query }).then((interactions) => {
            let tmpInteractions = interactions.filter((interaction) => !interaction.tags.includes("default"))
            if (status === undefined) {
                const interactionsMap = tmpInteractions.reduce<Record<string, InteractionRef[]>>((acc, item) => {
                    const key = item.endpoint
                    if (!acc[key]) {
                        acc[key] = []
                    }
                    acc[key].push(item)
                    return acc
                }, {})

                tmpInteractions = Object.values(interactionsMap).map((interactions) => {
                    const publishedVersions = interactions.filter((item) => item.status === InteractionStatus.published)
                    if (publishedVersions.length > 0) {
                        return publishedVersions.reduce((latest, current) => (current.version > latest.version ? current : latest))
                    } else {
                        return interactions.reduce((latest, current) => (current.version > latest.version ? current : latest))
                    }
                })
            }
            setInteractions(tmpInteractions)
        })
    }

    useEffect(() => {
        if (!open) {
            setFilterMode(undefined)
            setTagsValue("")
            setVersionValue(0)
        }
    }, [open])

    useEffect(() => {
        if (!client || isLoading || interactions.length > 0) {
            return
        }

        searchInteractions()
    }, [client, isLoading])

    useEffect(() => {
        if (!client || isLoading) {
            return
        }

        searchInteractions()
    }, [tags, version, status])

    useEffect(() => {
        if (filteredOptions.length === 0) {
            setUniqueTags([])
            return
        }
        const filteredTags = [...new Set(filteredOptions.flatMap((interaction) => interaction.tags || []))].sort()
        setUniqueTags(filteredTags)
    }, [filteredOptions])

    if (!isLoading && !user) {
        return null
    }

    return (
        <div className="w-full h-full overflow-hidden flex flex-col">
            <div className="flex flex-col items-center gap-6 flex-shrink-0">
                <div className="flex flex-col w-full border border-gray-300 rounded-lg p-2 gap-2">
                    <div className="flex flex-row items-center gap-2 flex-1">
                        <Input value={filterValue} onChange={(e) => setFilterValue(e.target.value)} onClear={() => setFilterValue("")} placeholder="Filter services..." className="flex-1 w-full border-none focus-visible:ring-0 focus:ring-0 focus:outline-none outline-none rounded-none shadow-none resize-none" />
                        {(tags.length === 0 || version === undefined || status === undefined) && (
                            <Popover open={open} onOpenChange={setOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" className={`transition group flex gap-1.5 ${open ? "bg-gray-200 text-accent-foreground" : ""}`}>
                                        <ListFilter />
                                        Filters
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className={`p-1 ${filterMode === "tags" ? "w-[350px]" : filterMode === "version" || filterMode === "status" ? "w-[150px]" : "w-[100px]"}`} align="end">
                                    <div className="flex flex-col gap-1 text-start">
                                        {!filterMode && (
                                            <>
                                                {status === undefined && (
                                                    <Button variant="ghost" className="flex flex-row items-center justify-end" onClick={() => setFilterMode("status")}>
                                                        Status
                                                    </Button>
                                                )}
                                                {tags.length === 0 && (
                                                    <Button variant="ghost" className="flex flex-row items-center justify-end" onClick={() => setFilterMode("tags")}>
                                                        Tags
                                                    </Button>
                                                )}
                                                {version === undefined && (
                                                    <Button variant="ghost" className="flex flex-row items-center justify-end" onClick={() => setFilterMode("version")}>
                                                        Version
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                        {filterMode === "tags" && (
                                            <div className="flex flex-col gap-2 p-2">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs italic text-muted-foreground">Enter tags separated by ",". Press enter when done</span>
                                                </div>
                                                <Input value={tagsValue} onChange={(e) => setTagsValue(e.target.value)} onKeyDown={handleTagsInputKeyDown} placeholder="e.g. agent, chat" className="w-full resize-none" />
                                            </div>
                                        )}
                                        {filterMode === "version" && (
                                            <div className="flex flex-col gap-2 p-2">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs italic text-muted-foreground">Enter version. Press enter when done</span>
                                                </div>
                                                <Input value={versionValue} onChange={handleVersionValueChange} onKeyDown={handleVersionInputKeyDown} placeholder="Enter version number" type="number" className="w-full resize-none" />
                                            </div>
                                        )}
                                        {filterMode === "status" && (
                                            <div className="flex flex-col gap-2 p-2">
                                                <Button variant="ghost" className="flex flex-row items-center justify-end" onClick={() => handleStatusChange(InteractionStatus.draft)}>
                                                    Draft
                                                </Button>
                                                <Button variant="ghost" className="flex flex-row items-center justify-end" onClick={() => handleStatusChange(InteractionStatus.published)}>
                                                    Published
                                                </Button>
                                                <Button variant="ghost" className="flex flex-row items-center justify-end" onClick={() => handleStatusChange(InteractionStatus.archived)}>
                                                    Archived
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        )}
                        {tags.length !== 0 && version !== undefined && status !== undefined && (
                            <Button variant="ghost" onClick={handleClearFilters}>
                                <FilterX />
                            </Button>
                        )}
                    </div>
                    {(tags.length > 0 || version !== undefined || status !== undefined) && (
                        <div className="flex flex-row items-center gap-4">
                            {status !== undefined && (
                                <div className="flex flex-row items-center gap-0.5">
                                    <Button variant="ghost" className="text-xs bg-muted rounded-r-none rounded-l-sm h-7 w-14">
                                        Status
                                    </Button>
                                    <Button variant="ghost" className="text-xs bg-muted rounded-r-none rounded-l-none h-7">
                                        {status}
                                    </Button>
                                    <Button variant="ghost" size="icon" className="bg-muted rounded-l-none rounded-r-sm size-7" onClick={() => setStatus(undefined)}>
                                        <X />
                                    </Button>
                                </div>
                            )}
                            {tags.length > 0 && (
                                <div className="flex flex-row items-center gap-0.5">
                                    <Button variant="ghost" className="text-xs bg-muted rounded-r-none rounded-l-sm h-7 w-10">
                                        Tags
                                    </Button>
                                    {tags.map((tag, index) => (
                                        <Button key={index} variant="ghost" className="text-xs bg-muted rounded-r-none rounded-l-none h-7" onClick={() => setTags(tags.filter((t) => t !== tag))}>
                                            {tag}
                                        </Button>
                                    ))}
                                    <Button variant="ghost" size="icon" className="bg-muted rounded-l-none rounded-r-sm size-7" onClick={() => setTags([])}>
                                        <X />
                                    </Button>
                                </div>
                            )}
                            {version !== undefined && (
                                <div className="flex flex-row items-center gap-0.5">
                                    <Button variant="ghost" className="text-xs bg-muted rounded-r-none rounded-l-sm h-7 w-14">
                                        Version
                                    </Button>
                                    <Button variant="ghost" className="text-xs bg-muted rounded-r-none rounded-l-none h-7">
                                        {version}
                                    </Button>
                                    <Button variant="ghost" size="icon" className="bg-muted rounded-l-none rounded-r-sm size-7" onClick={() => setVersion(undefined)}>
                                        <X />
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex flex-row gap-2 items-center py-2 overflow-x-auto scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {uniqueTags.map((tag, index) => (
                    <Badge key={index} onClick={() => handleUniqueTagSelection(tag)} variant={selectedTags.includes(tag) ? "default" : "secondary"} className="p-2 text-xs cursor-pointer hover:bg-gray-200 transition-colors duration-200 whitespace-nowrap flex-shrink-0">
                        {tag}
                    </Badge>
                ))}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-6 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredOptions.map((interaction) => (
                        <Card key={interaction.id} className="hover:shadow-lg transition-all duration-200 hover:cursor-pointer hover:scale-105 flex flex-col h-full" onClick={() => onInteractionSelected(interaction)}>
                            <CardHeader className="flex-shrink-0">
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <CardTitle className="text-lg font-semibold text-gray-900 truncate">{interaction.name}</CardTitle>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{interaction.name}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                {interaction.description && (
                                    <CardDescription className="text-sm text-gray-600 mt-2">
                                        {interaction.description.substring(0, 100)}
                                        {interaction.description.length > 100 ? "..." : ""}
                                    </CardDescription>
                                )}
                            </CardHeader>
                            <CardContent className="flex-grow flex flex-col justify-end">
                                <div className="space-y-3">
                                    {interaction.tags && interaction.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {interaction.tags.map((tag, index) => (
                                                <Badge key={index} variant="secondary" className="text-xs">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-500 space-y-1">
                                        <div>Version: {interaction.version}</div>
                                        <div>Status: {interaction.status}</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    )
}
